// SPDX-License-Identifier: BUSL-1.1

/**
 * Active sessions governance card — shows running session count, total cost,
 * and average duration for the agent governance dashboard row.
 *
 */

import type * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { apiFetch } from '@/lib/api-client';
import type { Session } from '@/types/api';

/** Approximate USD cost from token counts using default rates. */
function estimateCostUsd(session: Session): number {
  const INPUT_RATE = 3 / 1_000_000; // $3 per 1M input tokens
  const OUTPUT_RATE = 15 / 1_000_000; // $15 per 1M output tokens
  return session.inputTokens * INPUT_RATE + session.outputTokens * OUTPUT_RATE;
}

/** Format seconds into a human-readable duration string. */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function GovernanceSessionsCard(): React.ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ['agents', 'sessions', 'running'],
    queryFn: () => apiFetch<Session[]>('/agents/sessions?status=running'),
    refetchInterval: 15_000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Active Sessions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    );
  }

  const sessions = data ?? [];
  const count = sessions.length;
  const totalCost = sessions.reduce((sum, s) => sum + estimateCostUsd(s), 0);
  const avgDuration =
    count > 0
      ? sessions.reduce((sum, s) => sum + (s.activeSeconds ?? 0), 0) / count
      : 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Active Sessions</CardTitle>
        <Link
          to="/agents"
          className="text-xs text-neutral-500 transition hover:text-neutral-300"
        >
          View all →
        </Link>
      </CardHeader>

      <CardContent className="space-y-4">
        <div>
          <p className="font-mono text-2xl font-semibold text-neutral-100">
            {count}
          </p>
          <p className="text-xs text-neutral-500">running now</p>
        </div>

        <div className="flex gap-4">
          <div>
            <p className="font-mono text-sm text-green-400">
              ${totalCost.toFixed(2)}
            </p>
            <p className="text-[11px] text-neutral-500">total cost</p>
          </div>
          <div>
            <p className="font-mono text-sm text-blue-400">
              {count > 0 ? formatDuration(avgDuration) : '--'}
            </p>
            <p className="text-[11px] text-neutral-500">avg duration</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
